import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { artistDisplayName } from "../../utils/artistProfile";
import { artistsApi, budgetsApi, schedulesApi, tasksApi, usersApi, venuesApi } from "../../api";
import EventCostsPanel from "../../components/events/EventCostsPanel";
import { useEventDashboard } from "../../hooks/useEventDashboard";
import { CurrencyInput } from "../../components/CurrencyInput";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { QueryErrorBanner, TableSkeletonRows } from "../../components/feedback/QueryFeedback";
import { useAuth } from "../../context/AuthContext";
import { workspaceNav, writeLastEventId } from "../../routes/workspaceNavigation";
import { queryKeys } from "../../queryKeys";
import type { Artist, Budget, CompanyUserSummary, Schedule, Task, Venue } from "../../types";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import "./EventLogisticsPage.css";

const PRIORITY_OPTIONS = ["low", "medium", "high"].map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = ["open", "in_progress", "completed"].map((v) => ({ value: v, label: v }));

type Props = {
  workspace: WorkspaceId;
  embedMode?: boolean;
  defaultSection?: "budgets" | "tasks" | "schedules";
};

type BudgetForm = {
  category: string;
  estimated_cost: string;
  actual_cost: string;
};

type TaskForm = {
  description: string;
  assignee: string;
  assignee_user_id: string;
  due_date: string;
  priority: string;
  status: string;
};

type ScheduleForm = {
  artist_id: string;
  venue_id: string;
  scheduled_time: string;
};

const EventLogisticsPage: React.FC<Props> = ({ workspace, embedMode = false, defaultSection = "budgets" }) => {
  const { token } = useAuth();
  const { eventId } = useParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (eventId) {
      writeLastEventId(workspace, eventId);
    }
  }, [eventId, workspace]);

  const [activeSection, setActiveSection] = useState<"budgets" | "tasks" | "schedules">(defaultSection);
  // Shares the dashboard cache entry with Overview/P&L — no extra request.
  const { data: dashboardData } = useEventDashboard(workspace, eventId);

  const [budgetForm, setBudgetForm] = useState<BudgetForm>({ category: "", estimated_cost: "", actual_cost: "" });
  const [taskForm, setTaskForm] = useState<TaskForm>({
    description: "",
    assignee: "",
    assignee_user_id: "",
    due_date: "",
    priority: "medium",
    status: "open",
  });
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>({ artist_id: "", venue_id: "", scheduled_time: "" });
  const [budgetDrafts, setBudgetDrafts] = useState<Record<string, BudgetForm>>({});
  const [taskDrafts, setTaskDrafts] = useState<Record<string, TaskForm>>({});
  const [scheduleDrafts, setScheduleDrafts] = useState<Record<string, ScheduleForm>>({});
  const [pendingDelete, setPendingDelete] = useState<{
    kind: "budget" | "task" | "schedule";
    id: string;
  } | null>(null);

  const budgetsQuery = useQuery({
    queryKey: eventId && token ? queryKeys.budgets(workspace, eventId) : queryKeys.events.all(workspace),
    queryFn: () => budgetsApi.list(eventId!, token!),
    enabled: Boolean(eventId && token),
  });
  const tasksQuery = useQuery({
    queryKey: eventId && token ? queryKeys.tasks(workspace, eventId) : queryKeys.events.all(workspace),
    queryFn: () => tasksApi.list(eventId!, token!),
    enabled: Boolean(eventId && token),
  });
  const schedulesQuery = useQuery({
    queryKey: eventId && token ? queryKeys.schedules(workspace, eventId) : queryKeys.events.all(workspace),
    queryFn: () => schedulesApi.list(eventId!, token!),
    enabled: Boolean(eventId && token),
  });

  const artistsQuery = useQuery({
    queryKey: token ? queryKeys.artists.list(workspace) : queryKeys.artists.all(workspace),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });
  const venuesQuery = useQuery({
    queryKey: token ? queryKeys.venues.list(workspace) : queryKeys.venues.all(workspace),
    queryFn: () => venuesApi.list(token!),
    enabled: Boolean(token),
  });
  // Task assignee picker: only registered teammates can receive the "task
  // assigned" notification email, so the dropdown is scoped to the company
  // roster rather than free text.
  const companyMembersQuery = useQuery({
    queryKey: token ? queryKeys.companyMembers.list(workspace) : queryKeys.companyMembers.all(workspace),
    queryFn: () => usersApi.companyMembers({}, token!),
    enabled: Boolean(token),
  });

  const invalidateAll = async () => {
    if (!eventId) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.budgets(workspace, eventId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks(workspace, eventId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules(workspace, eventId) }),
    ]);
  };

  // Budget edits flow into the P&L engine on the backend (logistics is treated
  // as a committed cost), so the dashboard and EventOS workspace caches must
  // also be refreshed — otherwise the user sees stale total_cost / profit /
  // break-even numbers until React Query's stale-time elapses.
  const invalidatePnlSurfaces = async () => {
    if (!eventId) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.events.dashboard(workspace, eventId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.eventOs.workspace(workspace, eventId) }),
    ]);
  };

  const invalidateAfterBudgetMutation = async () => {
    await invalidateAll();
    await invalidatePnlSurfaces();
  };

  const budgetCreate = useMutation({
    mutationFn: async () => {
      if (!eventId || !token) throw new Error("Missing event context.");
      return budgetsApi.create(
        eventId,
        {
          category: budgetForm.category.trim(),
          estimated_cost: Number(budgetForm.estimated_cost) || 0,
          actual_cost: Number(budgetForm.actual_cost) || 0,
        },
        token
      );
    },
    onSuccess: invalidateAfterBudgetMutation,
  });

  const taskCreate = useMutation({
    mutationFn: async () => {
      if (!eventId || !token) throw new Error("Missing event context.");
      return tasksApi.create(
        eventId,
        {
          description: taskForm.description.trim(),
          assignee: taskForm.assignee.trim() || null,
          assignee_user_id: taskForm.assignee_user_id || null,
          due_date: taskForm.due_date || null,
          priority: taskForm.priority,
          status: taskForm.status,
        },
        token
      );
    },
    onSuccess: invalidateAll,
  });

  const scheduleCreate = useMutation({
    mutationFn: async () => {
      if (!eventId || !token) throw new Error("Missing event context.");
      const date = new Date(scheduleForm.scheduled_time);
      if (Number.isNaN(date.getTime())) {
        throw new Error("Invalid schedule date/time.");
      }
      return schedulesApi.create(
        eventId,
        {
          artist_id: scheduleForm.artist_id || null,
          venue_id: scheduleForm.venue_id || null,
          scheduled_time: date.toISOString(),
        },
        token
      );
    },
    onSuccess: invalidateAll,
  });

  const budgetDelete = useMutation({
    mutationFn: async (id: string) => {
      if (!eventId || !token) throw new Error("Missing event context.");
      return budgetsApi.remove(eventId, id, token);
    },
    onSuccess: invalidateAfterBudgetMutation,
  });
  const taskDelete = useMutation({
    mutationFn: async (id: string) => {
      if (!eventId || !token) throw new Error("Missing event context.");
      return tasksApi.remove(eventId, id, token);
    },
    onSuccess: invalidateAll,
  });
  const scheduleDelete = useMutation({
    mutationFn: async (id: string) => {
      if (!eventId || !token) throw new Error("Missing event context.");
      return schedulesApi.remove(eventId, id, token);
    },
    onSuccess: invalidateAll,
  });

  const budgetUpdate = useMutation({
    mutationFn: async (payload: { id: string; formData: BudgetForm }) => {
      if (!eventId || !token) throw new Error("Missing event context.");
      return budgetsApi.update(
        eventId,
        payload.id,
        {
          category: payload.formData.category.trim(),
          estimated_cost: Number(payload.formData.estimated_cost) || 0,
          actual_cost: Number(payload.formData.actual_cost) || 0,
        },
        token
      );
    },
    onSuccess: invalidateAfterBudgetMutation,
  });
  const taskUpdate = useMutation({
    mutationFn: async (payload: { id: string; formData: TaskForm }) => {
      if (!eventId || !token) throw new Error("Missing event context.");
      return tasksApi.update(
        eventId,
        payload.id,
        {
          description: payload.formData.description.trim(),
          assignee: payload.formData.assignee.trim() || null,
          assignee_user_id: payload.formData.assignee_user_id || null,
          due_date: payload.formData.due_date || null,
          priority: payload.formData.priority,
          status: payload.formData.status,
        },
        token
      );
    },
    onSuccess: invalidateAll,
  });
  const scheduleUpdate = useMutation({
    mutationFn: async (payload: { id: string; formData: ScheduleForm }) => {
      if (!eventId || !token) throw new Error("Missing event context.");
      const date = new Date(payload.formData.scheduled_time);
      if (Number.isNaN(date.getTime())) throw new Error("Invalid schedule date/time.");
      return schedulesApi.update(
        eventId,
        payload.id,
        {
          artist_id: payload.formData.artist_id || null,
          venue_id: payload.formData.venue_id || null,
          scheduled_time: date.toISOString(),
        },
        token
      );
    },
    onSuccess: invalidateAll,
  });

  const handleBudgetCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await budgetCreate.mutateAsync();
      setBudgetForm({ category: "", estimated_cost: "", actual_cost: "" });
      toast.success("Budget entry added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create budget");
    }
  };

  const handleTaskCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await taskCreate.mutateAsync();
      setTaskForm({ description: "", assignee: "", assignee_user_id: "", due_date: "", priority: "medium", status: "open" });
      toast.success("Task added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create task");
    }
  };

  const handleScheduleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await scheduleCreate.mutateAsync();
      setScheduleForm({ artist_id: "", venue_id: "", scheduled_time: "" });
      toast.success("Schedule added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create schedule");
    }
  };

  const requestDelete = (kind: "budget" | "task" | "schedule", id: string) => {
    setPendingDelete({ kind, id });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { kind, id } = pendingDelete;
    try {
      if (kind === "budget") await budgetDelete.mutateAsync(id);
      if (kind === "task") await taskDelete.mutateAsync(id);
      if (kind === "schedule") await scheduleDelete.mutateAsync(id);
      toast.success("Item deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setPendingDelete(null);
    }
  };

  const handleUpdate = async (kind: "budget" | "task" | "schedule", id: string) => {
    try {
      if (kind === "budget" && budgetDrafts[id]) {
        await budgetUpdate.mutateAsync({ id, formData: budgetDrafts[id] });
      }
      if (kind === "task" && taskDrafts[id]) {
        await taskUpdate.mutateAsync({ id, formData: taskDrafts[id] });
      }
      if (kind === "schedule" && scheduleDrafts[id]) {
        await scheduleUpdate.mutateAsync({ id, formData: scheduleDrafts[id] });
      }
      toast.success("Item updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    }
  };

  if (!eventId) {
    return (
      <div className="app-shell-dash event-logistics-page">
        <h1 className="app-shell-page-title">Event logistics</h1>
        <p className="app-shell-lede">Open this page from a specific event dashboard.</p>
      </div>
    );
  }

  return (
    <div className="app-shell-dash event-logistics-page">
      {!embedMode ? (
        <>
          <Link to={workspaceNav.dashboard(workspace, eventId!)} className="app-shell-back-link">
            ← Back to dashboard
          </Link>
          <div className="app-shell-dash-toolbar">
            <div>
              <h1 className="app-shell-page-title event-logistics-page__title">
                Event logistics
              </h1>
              <p className="app-shell-lede event-logistics-page__lede">
                Budgets, tasks, and schedules in one operational workspace.
              </p>
            </div>
            <div className="app-shell-refresh-row">
              <button type="button" className={`eventos-nav-btn${activeSection === "budgets" ? " active" : ""}`} onClick={() => setActiveSection("budgets")}>
                Budgets
              </button>
              <button type="button" className={`eventos-nav-btn${activeSection === "tasks" ? " active" : ""}`} onClick={() => setActiveSection("tasks")}>
                Tasks
              </button>
              <button type="button" className={`eventos-nav-btn${activeSection === "schedules" ? " active" : ""}`} onClick={() => setActiveSection("schedules")}>
                Schedules
              </button>
            </div>
          </div>
        </>
      ) : null}

      {(budgetsQuery.isError || tasksQuery.isError || schedulesQuery.isError) ? (
        <QueryErrorBanner
          message="One or more logistics sections failed to load."
          onRetry={() => {
            void budgetsQuery.refetch();
            void tasksQuery.refetch();
            void schedulesQuery.refetch();
          }}
        />
      ) : null}

      {/* Sprint 14: moved off Overview, which the sprint keeps uncluttered.
          Cost editing belongs with the rest of the cost tooling. */}
      {activeSection === "budgets" && eventId && dashboardData ? (
        <EventCostsPanel
          workspace={workspace}
          eventId={eventId}
          finances={dashboardData.event.finances}
        />
      ) : null}

      {activeSection === "budgets" ? (
        <div className="app-shell-dash-panel">
          <div className="app-shell-dash-panel-head">
            <h2 className="app-shell-dash-panel-title">Budgets</h2>
          </div>
          <div className="app-shell-dash-panel-body app-shell-dash-panel-body--padded">
            <form onSubmit={handleBudgetCreate} className="eventos-form">
              <div className="eventos-form-grid">
                <label className="eventos-field">
                  <span className="eventos-field-label">Category</span>
                  <input className="eventos-input" value={budgetForm.category} onChange={(e) => setBudgetForm((prev) => ({ ...prev, category: e.target.value }))} required />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Estimated cost</span>
                  <CurrencyInput value={budgetForm.estimated_cost} onChange={(e) => setBudgetForm((prev) => ({ ...prev, estimated_cost: e.target.value }))} required />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Actual cost</span>
                  <CurrencyInput value={budgetForm.actual_cost} onChange={(e) => setBudgetForm((prev) => ({ ...prev, actual_cost: e.target.value }))} required />
                </label>
              </div>
              <button className="eventos-nav-btn" type="submit" disabled={budgetCreate.isPending}>
                {budgetCreate.isPending ? "Adding…" : "Add budget"}
              </button>
            </form>
            <div className="app-shell-table-wrap event-logistics-page__table-wrap">
              <table className="app-shell-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Estimated</th>
                    <th>Actual</th>
                    <th className="event-logistics-page__actions-head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetsQuery.isPending ? (
                    <TableSkeletonRows rows={4} cols={4} />
                  ) : (budgetsQuery.data ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="event-logistics-page__empty-cell">
                        No budget rows yet.
                      </td>
                    </tr>
                  ) : (
                    (budgetsQuery.data ?? []).map((budget: Budget) => (
                      <tr key={budget.id}>
                        <td>
                          <input
                            className="eventos-input"
                            value={budgetDrafts[budget.id]?.category ?? budget.category}
                            onChange={(e) =>
                              setBudgetDrafts((prev) => ({
                                ...prev,
                                [budget.id]: {
                                  category: e.target.value,
                                  estimated_cost: prev[budget.id]?.estimated_cost ?? String(budget.estimated_cost),
                                  actual_cost: prev[budget.id]?.actual_cost ?? String(budget.actual_cost),
                                },
                              }))
                            }
                          />
                        </td>
                        <td>
                          <CurrencyInput
                            value={budgetDrafts[budget.id]?.estimated_cost ?? String(budget.estimated_cost)}
                            onChange={(e) =>
                              setBudgetDrafts((prev) => ({
                                ...prev,
                                [budget.id]: {
                                  category: prev[budget.id]?.category ?? budget.category,
                                  estimated_cost: e.target.value,
                                  actual_cost: prev[budget.id]?.actual_cost ?? String(budget.actual_cost),
                                },
                              }))
                            }
                          />
                        </td>
                        <td>
                          <CurrencyInput
                            value={budgetDrafts[budget.id]?.actual_cost ?? String(budget.actual_cost)}
                            onChange={(e) =>
                              setBudgetDrafts((prev) => ({
                                ...prev,
                                [budget.id]: {
                                  category: prev[budget.id]?.category ?? budget.category,
                                  estimated_cost: prev[budget.id]?.estimated_cost ?? String(budget.estimated_cost),
                                  actual_cost: e.target.value,
                                },
                              }))
                            }
                          />
                        </td>
                        <td>
                          <div className="app-shell-table-actions">
                            <button type="button" className="eventos-nav-btn" onClick={() => void handleUpdate("budget", budget.id)} disabled={budgetUpdate.isPending}>
                              Save
                            </button>
                            <button type="button" className="eventos-nav-btn" onClick={() => requestDelete("budget", budget.id)} disabled={budgetDelete.isPending}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {activeSection === "tasks" ? (
        <div className="app-shell-dash-panel">
          <div className="app-shell-dash-panel-head">
            <h2 className="app-shell-dash-panel-title">Tasks</h2>
          </div>
          <div className="app-shell-dash-panel-body app-shell-dash-panel-body--padded">
            <form onSubmit={handleTaskCreate} className="eventos-form">
              <div className="eventos-form-grid">
                <label className="eventos-field">
                  <span className="eventos-field-label">Description</span>
                  <input className="eventos-input" value={taskForm.description} onChange={(e) => setTaskForm((prev) => ({ ...prev, description: e.target.value }))} required />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Assignee (display name)</span>
                  <input className="eventos-input" value={taskForm.assignee} onChange={(e) => setTaskForm((prev) => ({ ...prev, assignee: e.target.value }))} />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Notify teammate</span>
                  <MobileSelect
                    label="Notify teammate"
                    className="eventos-select"
                    value={taskForm.assignee_user_id}
                    onChange={(v) => setTaskForm((prev) => ({ ...prev, assignee_user_id: v }))}
                    options={[
                      { value: "", label: "No email notification" },
                      ...(companyMembersQuery.data ?? []).map((member: CompanyUserSummary) => ({
                        value: member.id,
                        label: member.full_name,
                      })),
                    ]}
                  />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Due date</span>
                  <input
                    className="eventos-input"
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm((prev) => ({ ...prev, due_date: e.target.value }))}
                  />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Priority</span>
                  <MobileSelect
                    label="Priority"
                    className="eventos-select"
                    value={taskForm.priority}
                    onChange={(v) => setTaskForm((prev) => ({ ...prev, priority: v }))}
                    options={PRIORITY_OPTIONS}
                  />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Status</span>
                  <MobileSelect
                    label="Status"
                    className="eventos-select"
                    value={taskForm.status}
                    onChange={(v) => setTaskForm((prev) => ({ ...prev, status: v }))}
                    options={STATUS_OPTIONS}
                  />
                </label>
              </div>
              <button className="eventos-nav-btn" type="submit" disabled={taskCreate.isPending}>
                {taskCreate.isPending ? "Adding…" : "Add task"}
              </button>
            </form>
            <div className="app-shell-table-wrap event-logistics-page__table-wrap">
              <table className="app-shell-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Assignee</th>
                    <th>Notify</th>
                    <th>Due date</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th className="event-logistics-page__actions-head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasksQuery.isPending ? (
                    <TableSkeletonRows rows={4} cols={7} />
                  ) : (tasksQuery.data ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="event-logistics-page__empty-cell">
                        No tasks yet.
                      </td>
                    </tr>
                  ) : (
                    (tasksQuery.data ?? []).map((task: Task) => {
                      const draft: TaskForm = {
                        description: taskDrafts[task.id]?.description ?? task.description,
                        assignee: taskDrafts[task.id]?.assignee ?? task.assignee ?? "",
                        assignee_user_id: taskDrafts[task.id]?.assignee_user_id ?? task.assignee_user_id ?? "",
                        due_date: taskDrafts[task.id]?.due_date ?? task.due_date ?? "",
                        priority: taskDrafts[task.id]?.priority ?? task.priority ?? "medium",
                        status: taskDrafts[task.id]?.status ?? task.status,
                      };
                      const setDraft = (patch: Partial<TaskForm>) =>
                        setTaskDrafts((prev) => ({ ...prev, [task.id]: { ...draft, ...patch } }));
                      return (
                        <tr key={task.id}>
                          <td>
                            <input className="eventos-input" value={draft.description} onChange={(e) => setDraft({ description: e.target.value })} />
                          </td>
                          <td>
                            <input className="eventos-input" value={draft.assignee} onChange={(e) => setDraft({ assignee: e.target.value })} />
                          </td>
                          <td>
                            <MobileSelect
                              label="Notify teammate"
                              className="eventos-select"
                              value={draft.assignee_user_id}
                              onChange={(v) => setDraft({ assignee_user_id: v })}
                              options={[
                                { value: "", label: "No email notification" },
                                ...(companyMembersQuery.data ?? []).map((member: CompanyUserSummary) => ({
                                  value: member.id,
                                  label: member.full_name,
                                })),
                              ]}
                            />
                          </td>
                          <td>
                            <input className="eventos-input" type="date" value={draft.due_date} onChange={(e) => setDraft({ due_date: e.target.value })} />
                          </td>
                          <td>
                            <MobileSelect
                              label="Priority"
                              className="eventos-select"
                              value={draft.priority}
                              onChange={(v) => setDraft({ priority: v })}
                              options={PRIORITY_OPTIONS}
                            />
                          </td>
                          <td>
                            <MobileSelect
                              label="Status"
                              className="eventos-select"
                              value={draft.status}
                              onChange={(v) => setDraft({ status: v })}
                              options={STATUS_OPTIONS}
                            />
                          </td>
                          <td>
                            <div className="app-shell-table-actions">
                              <button type="button" className="eventos-nav-btn" onClick={() => void handleUpdate("task", task.id)} disabled={taskUpdate.isPending}>
                                Save
                              </button>
                              <button type="button" className="eventos-nav-btn" onClick={() => requestDelete("task", task.id)} disabled={taskDelete.isPending}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {activeSection === "schedules" ? (
        <div className="app-shell-dash-panel">
          <div className="app-shell-dash-panel-head">
            <h2 className="app-shell-dash-panel-title">Schedules</h2>
          </div>
          <div className="app-shell-dash-panel-body app-shell-dash-panel-body--padded">
            <form onSubmit={handleScheduleCreate} className="eventos-form">
              <div className="eventos-form-grid">
                <label className="eventos-field">
                  <span className="eventos-field-label">Artist</span>
                  <MobileSelect
                    label="Artist"
                    className="eventos-select"
                    value={scheduleForm.artist_id}
                    onChange={(v) => setScheduleForm((prev) => ({ ...prev, artist_id: v }))}
                    options={[
                      { value: "", label: "Select artist" },
                      ...(artistsQuery.data ?? []).map((artist: Artist) => ({
                        value: artist.id,
                        label: artistDisplayName(artist),
                      })),
                    ]}
                  />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Venue</span>
                  <MobileSelect
                    label="Venue"
                    className="eventos-select"
                    value={scheduleForm.venue_id}
                    onChange={(v) => setScheduleForm((prev) => ({ ...prev, venue_id: v }))}
                    options={[
                      { value: "", label: "Select venue" },
                      ...(venuesQuery.data ?? []).map((venue: Venue) => ({
                        value: venue.id,
                        label: venue.venue_name,
                      })),
                    ]}
                  />
                </label>
                <label className="eventos-field">
                  <span className="eventos-field-label">Scheduled time</span>
                  <input
                    className="eventos-input"
                    type="datetime-local"
                    value={scheduleForm.scheduled_time}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, scheduled_time: e.target.value }))}
                    required
                  />
                </label>
              </div>
              <button className="eventos-nav-btn" type="submit" disabled={scheduleCreate.isPending}>
                {scheduleCreate.isPending ? "Adding…" : "Add schedule"}
              </button>
            </form>
            <div className="app-shell-table-wrap event-logistics-page__table-wrap">
              <table className="app-shell-table">
                <thead>
                  <tr>
                    <th>Artist</th>
                    <th>Venue</th>
                    <th>Scheduled time</th>
                    <th className="event-logistics-page__actions-head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedulesQuery.isPending ? (
                    <TableSkeletonRows rows={4} cols={4} />
                  ) : (schedulesQuery.data ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="event-logistics-page__empty-cell">
                        No schedules yet.
                      </td>
                    </tr>
                  ) : (
                    (schedulesQuery.data ?? []).map((schedule: Schedule) => (
                      <tr key={schedule.id}>
                        <td>
                          <MobileSelect
                            label="Artist"
                            className="eventos-select"
                            value={scheduleDrafts[schedule.id]?.artist_id ?? schedule.artist_id ?? ""}
                            onChange={(v) =>
                              setScheduleDrafts((prev) => ({
                                ...prev,
                                [schedule.id]: {
                                  artist_id: v,
                                  venue_id: scheduleDrafts[schedule.id]?.venue_id ?? schedule.venue_id ?? "",
                                  scheduled_time: scheduleDrafts[schedule.id]?.scheduled_time ?? new Date(schedule.scheduled_time).toISOString().slice(0, 16),
                                },
                              }))
                            }
                            options={[
                              { value: "", label: "Unassigned" },
                              ...(artistsQuery.data ?? []).map((artist: Artist) => ({
                                value: artist.id,
                                label: artistDisplayName(artist),
                              })),
                            ]}
                          />
                        </td>
                        <td>
                          <MobileSelect
                            label="Venue"
                            className="eventos-select"
                            value={scheduleDrafts[schedule.id]?.venue_id ?? schedule.venue_id ?? ""}
                            onChange={(v) =>
                              setScheduleDrafts((prev) => ({
                                ...prev,
                                [schedule.id]: {
                                  artist_id: scheduleDrafts[schedule.id]?.artist_id ?? schedule.artist_id ?? "",
                                  venue_id: v,
                                  scheduled_time: scheduleDrafts[schedule.id]?.scheduled_time ?? new Date(schedule.scheduled_time).toISOString().slice(0, 16),
                                },
                              }))
                            }
                            options={[
                              { value: "", label: "Unassigned" },
                              ...(venuesQuery.data ?? []).map((venue: Venue) => ({
                                value: venue.id,
                                label: venue.venue_name,
                              })),
                            ]}
                          />
                        </td>
                        <td>
                          <input
                            className="eventos-input"
                            type="datetime-local"
                            value={scheduleDrafts[schedule.id]?.scheduled_time ?? new Date(schedule.scheduled_time).toISOString().slice(0, 16)}
                            onChange={(e) =>
                              setScheduleDrafts((prev) => ({
                                ...prev,
                                [schedule.id]: {
                                  artist_id: scheduleDrafts[schedule.id]?.artist_id ?? schedule.artist_id ?? "",
                                  venue_id: scheduleDrafts[schedule.id]?.venue_id ?? schedule.venue_id ?? "",
                                  scheduled_time: e.target.value,
                                },
                              }))
                            }
                          />
                        </td>
                        <td>
                          <div className="app-shell-table-actions">
                            <button
                              type="button"
                              className="eventos-nav-btn"
                              onClick={() => void handleUpdate("schedule", schedule.id)}
                              disabled={scheduleUpdate.isPending}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="eventos-nav-btn"
                              onClick={() => requestDelete("schedule", schedule.id)}
                              disabled={scheduleDelete.isPending}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete item?"
        message="This row will be removed from the event workspace."
        confirmLabel="Delete"
        danger
        loading={budgetDelete.isPending || taskDelete.isPending || scheduleDelete.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default EventLogisticsPage;
